/** Category-driven setup profiles for Project Wizard + Settings. */

export const PROJECT_CATEGORY_OPTIONS = [
  { value: 'single_building', label: 'Single Building' },
  { value: 'multiple_buildings', label: 'Multiple Buildings' },
  { value: 'civil', label: 'Civil' },
];

/** Legacy values stored on existing projects → canonical profile key. */
export const PROJECT_CATEGORY_ALIASES = {
  multi_building_site: 'multiple_buildings',
  multi_discipline: 'multiple_buildings',
  site_infrastructure: 'civil',
  bridge_structure: 'civil',
};

export const CIVIL_PROJECT_CATEGORY = 'civil';

export function isCivilCategory(category) {
  return normalizeProjectCategory(category) === CIVIL_PROJECT_CATEGORY;
}

const BUILDING_FIELDS = [
  'occupancy',
  'majorOccupancy',
  'classification',
  'storeysAboveGrade',
  'storeysBelowGrade',
  'buildingFootprintArea',
  'grossFloorArea',
  'buildingHeight',
  'adjacentStreets',
  'averageGrade',
  'constructionType',
  'compliance',
];

const CIVIL_SCOPE_FIELDS = ['civilVerticals', 'scopeOfWork'];

const INFRA_FIELDS = [
  ...CIVIL_SCOPE_FIELDS,
  'elementName',
  'infraTypes',
  'primaryInfraType',
  'scope',
  'designStandard',
  'linearExtent',
  'area',
  'rowWidth',
  'networkDiameter',
  'burialDepth',
  'materialContext',
  'environmental',
];

export const PROJECT_CATEGORY_FIELD_PROFILES = {
  single_building: {
    key: 'single_building',
    title: 'Building',
    description: 'Occupancy, metrics, and compliance for this building',
    tocLabel: 'Building',
    entityNameLabel: 'Building Name',
    showTabs: false,
    showAddButton: false,
    showPanelActions: false,
    allowMultipleEntities: false,
    structureKindDefault: 'building',
    visibleFields: BUILDING_FIELDS,
    defaultEntities: [{ name: 'Primary Building', structureKind: 'building' }],
  },
  multiple_buildings: {
    key: 'multiple_buildings',
    title: 'Buildings on Site',
    description: 'Each building on this site has its own occupancy, code parameters, and optional discipline scope',
    tocLabel: 'Buildings on Site',
    entityNameLabel: 'Building Name',
    addEntityButtonLabel: '+ Add Additional Building',
    showTabs: true,
    showAddButton: true,
    showPanelActions: true,
    allowMultipleEntities: true,
    structureKindDefault: 'building',
    visibleFields: [...BUILDING_FIELDS, 'disciplines'],
    defaultEntities: [
      { name: 'Building A', structureKind: 'building' },
      { name: 'Building B', structureKind: 'building' },
      { name: 'Parking P1', structureKind: 'structure' },
    ],
  },
  civil: {
    key: 'civil',
    title: 'Civil Scope',
    description: 'Scope civil work by industry vertical. Add roads, utilities, and element detail in Project Settings as documents arrive.',
    tocLabel: 'Civil Scope',
    entityNameLabel: 'Element Name',
    addEntityButtonLabel: '+ Add Additional Element',
    showTabs: true,
    showAddButton: true,
    showPanelActions: true,
    allowMultipleEntities: true,
    structureKindDefault: 'site',
    visibleFields: INFRA_FIELDS,
    civilTwoStepWizard: true,
    wizardMetricsRequired: false,
    wizardComplianceRequired: false,
    defaultEntities: [
      { name: 'Civil Scope', structureKind: 'site', civilVerticals: [], scopeOfWork: '' },
    ],
  },
};

export const DEFAULT_PROJECT_CATEGORY = 'single_building';

export function normalizeProjectCategory(category) {
  if (!category) return DEFAULT_PROJECT_CATEGORY;
  return PROJECT_CATEGORY_ALIASES[category] || category;
}

export function getProjectCategoryProfile(category) {
  const key = normalizeProjectCategory(category);
  return PROJECT_CATEGORY_FIELD_PROFILES[key] || PROJECT_CATEGORY_FIELD_PROFILES[DEFAULT_PROJECT_CATEGORY];
}

export function isFieldVisible(profile, fieldKey) {
  if (!profile?.visibleFields?.length) return true;
  return profile.visibleFields.includes(fieldKey);
}
